import {
  Stores,
  User,
  Email,
  Comunication,
  PersonalObjective,
  GenericObjective,
  Evaluation,
  Subscription,
  Payment,
  Activity,
  Reservation,
} from "../types";
import api from "../services/api";
import { makeAutoObservable } from "mobx";
import dayjs from "dayjs";

export class ClientsStore {
  stores: Stores;

  clients: User[] = [];
  admins: User[] = [];
  customerData: User | null = null;
  customerEmails: Email[] = [];
  comunications: Comunication[] = [];
  personalObjective: PersonalObjective | null = null;
  genericObjectives: GenericObjective[] = [];
  evaluation: Evaluation[] = [];
  isLoading = false;
  isSubmitting = false;

  isUpdatingCustomerProfile = false;

  customerSubscription: Subscription | null = null;
  customerPaymentsList: Payment[] = [];
  stripeSubscriptionError = false;
  stripePaymentsError = false;
  customerActivities: Activity[] = [];
  customerReservations: Reservation[] = [];

  clientsFilters: {
    [dataKey: string]: string | string[];
  } = {};

  clientsSubscriptionCancellation: User[] = [];
  clientsEmptyWeight: User[] = [];
  clientsEmptyEvaluation: User[] = [];
  clientsSubscriptionTrial: User[] = [];
  clientsFewWorkouts: User[] = [];

  constructor(stores: Stores) {
    this.stores = stores;

    makeAutoObservable(this);
  }

  getClients = async () => {
    this.isLoading = true;
    try {
      let clients = await api.getClients();
      this.clients = clients;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getAdmins = async () => {
    this.isLoading = true;
    try {
      let admins = await api.getAdmins();
      this.admins = admins.filter(
        (admin) => admin.id !== this.stores.session.user?.id
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getClient = async (user_id: string) => {
    this.isLoading = true;
    // reset all data
    this.customerData = null;
    this.customerSubscription = null;
    this.customerPaymentsList = [];
    try {
      const customerData = await api.getClient(user_id);
      this.customerData = customerData;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  addCustomer = async (data: Partial<User>) => {
    this.isSubmitting = true;
    try {
      const newCustomer = await api.addCustomer(data);
      this.clients.unshift(newCustomer);
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  addAdmin = async (data: Partial<User>) => {
    this.isSubmitting = true;
    try {
      const newAdmin = await api.addAdmin(data);

      this.admins.unshift(newAdmin);
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  deleteAdmin = async (user_id: User["id"]) => {
    try {
      let deletedAdmin = await api.deleteAdmin(user_id);

      this.admins = this.admins.filter((admin) => admin.id !== deletedAdmin.id);
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  updateCustomer = async (user_id: User["id"], data: Partial<User>) => {
    try {
      this.isUpdatingCustomerProfile = true;

      const userData = await api.updateCustomerPersonalData(user_id, data);

      if (user_id === this.stores.session.user?.id) {
        this.stores.session.user = { ...this.stores.session.user, ...userData };
      }
    } catch (err) {
      console.log(err);
    } finally {
      this.isUpdatingCustomerProfile = false;
    }
  };

  getClientsSubscriptionCancellation = async () => {
    this.isLoading = true;
    try {
      let clientsSubscriptionCancellation =
        await api.getClientsSubscriptionCancellation();

      this.clientsSubscriptionCancellation = clientsSubscriptionCancellation;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getClientsEmptyWeight = async () => {
    this.isLoading = true;
    try {
      let clientsEmptyWeight = await api.getClientsEmptyWeight();
      this.clientsEmptyWeight = clientsEmptyWeight;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getClientsEmptyEvaluation = async () => {
    this.isLoading = true;
    try {
      let clientsEmptyEvaluation = await api.getClientsEmptyEvaluation();

      this.clientsEmptyEvaluation = clientsEmptyEvaluation;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getClientsSubscriptionTrial = async () => {
    this.isLoading = true;
    try {
      let clientsSubscriptionTrial = await api.getClientsSubscriptionTrial();

      this.clientsSubscriptionTrial = clientsSubscriptionTrial;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  getClientsFewWorkouts = async (numDays: number) => {
    this.isLoading = true;
    try {
      let clientsFewWorkouts = await api.getClientsFewWorkouts(numDays);

      this.clientsFewWorkouts = clientsFewWorkouts;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  //EMAILS
  getEmail = async (userId: string) => {
    this.isLoading = true;
    this.customerEmails = [];
    try {
      const email = await api.getEmail(userId);
      this.customerEmails = email;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  addEmail = async (userId: string, data: Partial<Email>) => {
    this.isSubmitting = true;
    try {
      data.userId = userId;
      let email = await api.addEmail(data);

      this.customerEmails = [email, ...this.customerEmails];
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  updateEmail = async (userId: string, data: Partial<Email>) => {
    this.isSubmitting = true;
    try {
      data.userId = userId;
      let updatedEmail = await api.updateEmail(data);

      this.customerEmails = this.customerEmails.map((email) =>
        email.id === updatedEmail.id ? updatedEmail : email
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  deleteEmail = async (data: Email) => {
    this.isSubmitting = true;
    try {
      let deletedEmail = await api.deleteEmail(data);

      this.customerEmails = this.customerEmails.filter(
        (email) => email.id !== deletedEmail.id
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  // COMUNICATIONS
  getComunication = async (userId: string) => {
    this.isLoading = true;
    this.comunications = [];
    try {
      let comunications = await api.getComunication(userId);
      this.comunications = comunications;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isLoading = false;
    }
  };

  //ADD COMUNICATION
  addComunication = async (user_id: string, data: Partial<Comunication>) => {
    this.isSubmitting = true;
    try {
      delete data.id;
      data.userId = user_id;
      let comunication = await api.addComunication(data);
      this.comunications = [comunication, ...this.comunications];
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  updateComunication = async (userId: string, data: Partial<Comunication>) => {
    this.isSubmitting = true;
    try {
      data.userId = userId;
      let updatedComunication = await api.updateComunication(data);

      this.comunications = this.comunications.map((comunication) =>
        comunication.id === updatedComunication.id
          ? updatedComunication
          : comunication
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  deleteComunication = async (data: any) => {
    this.isSubmitting = true;
    try {
      let deletedComunication = await api.deleteComunication(data);

      this.comunications = this.comunications.filter(
        (comunication) => comunication.id !== deletedComunication.id
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  // PERSONAL OBJECTIVE
  //GET PERSONAL OBJECTIVE
  getPersonalObjective = async (user_id: string) => {
    try {
      let personalObjective = await api.getPersonalObjective(user_id);

      personalObjective
        ? (this.personalObjective = personalObjective)
        : (this.personalObjective = null);
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  //ADD PERSONAL OBJECTIVE
  addPersonalObjective = async (user_id: string, data: any) => {
    try {
      delete data.id;
      data.userId = user_id;
      let personalObjective = await api.addPersonalObjective(data);
      this.personalObjective = personalObjective;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  //UPDATE PERSONAL OBJECTIVE
  updatePersonalObjective = async (user_id: string, data: any) => {
    try {
      data.userId = user_id;
      let personalObjective: PersonalObjective =
        await api.updatePersonalObjective(data);
      this.personalObjective = personalObjective;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  // GENERIC OBJECTIVE

  //GET GENERIC OBJECTIVE
  getGenericObjective = async (user_id: string) => {
    try {
      let genericObjectives = await api.getGenericObjective(user_id);
      this.genericObjectives = genericObjectives;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  //GET GENERIC OBJECTIVE BY ID
  getGenericObjectiveById = async (id: string) => {
    try {
      let genericObjectives = await api.getGenericObjectiveById(id);
      return genericObjectives;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  addGenericObjective = async (user_id: string, data: any) => {
    try {
      delete data.id;
      data.userId = user_id;
      let genericObjectives = await api.addGenericObjective(data);
      this.genericObjectives = [...this.genericObjectives, genericObjectives];
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  updateGenericObjective = async (user_id: string, data: any) => {
    try {
      data.userId = user_id;
      const updatedGenericObjective = await api.updateGenericObjective(data);

      this.genericObjectives = this.genericObjectives.map((genericObjective) =>
        genericObjective.id === updatedGenericObjective.id
          ? updatedGenericObjective
          : genericObjective
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  // EVALUATION

  //GET EVALUATION
  getEvaluation = async (userId: User["id"]) => {
    try {
      let evaluation = await api.getEvaluation(userId);
      this.evaluation = evaluation;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  //ADD EVALUATION
  addEvaluation = async (userId: User["id"], data: any) => {
    try {
      delete data.id;
      data.userId = userId;
      let evaluation = await api.addEvaluation(data);
      this.evaluation = [evaluation, ...this.evaluation];
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  updateEvaluation = async (data: any) => {
    try {
      let updatedEvaluation = await api.updateEvaluation(data);
      this.evaluation = this.evaluation.map((evaluation) =>
        evaluation.id === updatedEvaluation.id ? updatedEvaluation : evaluation
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  deleteEvaluation = async (data: any) => {
    try {
      let deletedEvaluation = await api.deleteEvaluation(data);
      this.evaluation = this.evaluation.filter(
        (evaluation) => evaluation.id !== deletedEvaluation.id
      );
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
    }
  };

  getStripeSubscription = async (subscriptionId: string) => {
    this.stripeSubscriptionError = false;
    this.isSubmitting = true;
    this.customerSubscription = null;

    try {
      const stripeSubscription = await api.stripeGetSubscription(
        subscriptionId
      );

      this.customerSubscription = stripeSubscription;
    } catch (err) {
      console.log(err);
      this.stripeSubscriptionError = true;
      return null;
    } finally {
      this.isSubmitting = false;
    }
  };

  getPayments = async (customerId: string) => {
    this.stripePaymentsError = false;

    try {
      const paymentsList = await api.stripeGetPayments(customerId);

      this.customerPaymentsList = paymentsList;
    } catch (err) {
      console.log(err);
      this.stripePaymentsError = true;
      return null;
    } finally {
      this.isSubmitting = false;
    }
  };

  fetchActivities = async (userId: User["id"]) => {
    this.customerActivities = [];
    try {
      const activities = await api.getActivities(
        userId,
        dayjs().hour(0).minute(0).second(0).subtract(6, "months").toDate(),
        dayjs().hour(0).minute(0).second(0).add(1, "day").toDate()
      );

      this.customerActivities = activities;
    } catch (err) {
      console.log(err);
    }
  };

  fetchReservations = async (userId: User["id"]) => {
    this.customerReservations = [];
    try {
      const reservations = await api.getReservations(
        userId,
        dayjs().hour(0).minute(0).second(0).subtract(6, "months").toDate(),
        dayjs().hour(0).minute(0).second(0).add(1, "day").toDate()
      );

      this.customerReservations = reservations;
    } catch (err) {
      console.log(err);
    }
  };

  removeActivity = async (activityId: number) => {
    try {
      const response = await api.removeActivity(activityId);

      this.customerActivities = this.customerActivities.filter(
        (activity) => activity.id !== response.id
      );
    } catch (err) {
      console.log(err);
    }
  };

  removeReservation = async (reservationId: number) => {
    try {
      const response = await api.removeReservation(reservationId);

      this.customerReservations = this.customerReservations.filter(
        (reservation) => reservation.id !== response.id
      );
    } catch (err) {
      console.log(err);
    }
  };

  updateProjectFilters = (dataKey: string, value: string) => {
    let newFilters = { ...this.clientsFilters };

    if (value?.length > 0) {
      newFilters[dataKey] = value;
    } else {
      delete newFilters[dataKey];
    }
    this.clientsFilters = newFilters;
  };

  cleanProjectFilters = () => {
    this.clientsFilters = {};
  };

  deleteClient = async (userId: User["id"]) => {
    this.isSubmitting = true;

    try {
      await api.deleteClient(userId);

      this.clients = this.clients.filter((client) => client.id !== userId);
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };
}
